import { useCallback, useEffect, useState } from 'react';
import { api, ErrorApi, type Invitacion, type Miembro } from '../api';
import { Tabla } from './Tabla';

/**
 * Administración del equipo del tenant.
 *
 * Solo para el rol admin, y eso se aplica en el portal: acá la pestaña se
 * oculta, pero los endpoints responden 403 igual si alguien los llama a mano.
 */

const ROLES = [
  { valor: 'lector', etiqueta: 'Lector — procesos y embudos' },
  { valor: 'analista', etiqueta: 'Analista — además el detalle técnico' },
  { valor: 'admin', etiqueta: 'Administrador — además equipo y auditoría' },
] as const;

function cuandoVence(iso: string): string {
  const dias = Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000);
  if (dias <= 0) return 'vence hoy';
  return `vence en ${dias} ${dias === 1 ? 'día' : 'días'}`;
}

export function Equipo({ onError }: { onError: (err: unknown) => void }) {
  const [miembros, setMiembros] = useState<Miembro[]>([]);
  const [invitaciones, setInvitaciones] = useState<Invitacion[]>([]);
  const [email, setEmail] = useState('');
  const [rol, setRol] = useState<string>('lector');
  const [aviso, setAviso] = useState<string | null>(null);
  const [errorInvitar, setErrorInvitar] = useState<string | null>(null);
  /** El enlace de la última invitación. Se muestra una vez; después solo va por correo. */
  const [enlaceNuevo, setEnlaceNuevo] = useState<{ enlace: string; correoEnviado: boolean } | null>(null);
  const [cargando, setCargando] = useState(false);
  const [cambiandoRolDe, setCambiandoRolDe] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const datos = await api.equipo();
      setMiembros(datos.miembros);
      setInvitaciones(datos.invitaciones);
    } catch (err) {
      onError(err);
    } finally {
      setCargando(false);
    }
  }, [onError]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function invitar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault();
    if (!email.trim()) return;

    setErrorInvitar(null);
    setAviso(null);
    try {
      const r = await api.invitar(email.trim(), rol);
      setEnlaceNuevo({ enlace: r.enlace, correoEnviado: r.correoEnviado });
      setAviso(
        r.correoEnviado
          ? `Invitación enviada a ${email.trim()}.`
          : `Invitación creada para ${email.trim()}, pero el servidor no tiene correo configurado: el enlace no salió. Pásaselo por otro medio.`
      );
      setEmail('');
      await cargar();
    } catch (err) {
      setErrorInvitar(err instanceof ErrorApi ? err.message : 'no se pudo invitar');
    }
  }

  async function revocar(id: string): Promise<void> {
    try {
      await api.revocarInvitacion(id);
      await cargar();
    } catch (err) {
      onError(err);
    }
  }

  async function cambiarRol(miembro: Miembro, nuevoRol: string): Promise<void> {
    if (nuevoRol === miembro.rol) return;
    setCambiandoRolDe(miembro.id);
    try {
      await api.cambiarRolMiembro(miembro.id, nuevoRol);
      await cargar();
    } catch (err) {
      onError(err);
    } finally {
      setCambiandoRolDe(null);
    }
  }

  async function quitar(miembro: Miembro): Promise<void> {
    // Cortar el acceso de alguien es irreversible desde acá: se confirma.
    if (!window.confirm(`¿Quitar a ${miembro.nombre} de este tenant? Se cerrarán sus sesiones.`)) return;
    try {
      await api.quitarMiembro(miembro.id);
      await cargar();
    } catch (err) {
      onError(err);
    }
  }

  return (
    <section className="equipo">
      <div className="equipo-columnas">
        <div>
          <h3>Miembros {cargando ? '' : `(${miembros.length})`}</h3>
          <Tabla>
            <thead>
              <tr>
                <th>Persona</th>
                <th>Rol</th>
                <th>2FA</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {miembros.map((m) => (
                <tr key={m.id}>
                  <td>
                    <strong>{m.nombre}</strong>
                    <span className="correo">{m.email}</span>
                  </td>
                  <td>
                    {m.esUnoMismo ? (
                      <span className={`marca rol-${m.rol}`}>{m.rol}</span>
                    ) : (
                      <select
                        className={`marca rol-${m.rol}`}
                        value={m.rol}
                        disabled={cambiandoRolDe === m.id}
                        onChange={(e) => void cambiarRol(m, e.target.value)}
                      >
                        {ROLES.map((r) => (
                          <option key={r.valor} value={r.valor}>
                            {r.valor}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    {m.segundoFactor ? (
                      <span className="si">sí</span>
                    ) : (
                      <span className="tenue">no</span>
                    )}
                  </td>
                  <td>
                    {m.esUnoMismo ? (
                      <span className="tenue">tú</span>
                    ) : (
                      <button className="enlace" onClick={() => void quitar(m)}>
                        quitar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabla>
          <p className="tenue">
            Cambiar el rol de alguien cierra sus sesiones abiertas: tiene que volver a entrar para
            que el permiso nuevo tome efecto.
          </p>

          {invitaciones.length > 0 && (
            <>
              <h3 className="con-espacio">Invitaciones pendientes</h3>
              <Tabla>
                <tbody>
                  {invitaciones.map((i) => (
                    <tr key={i.id}>
                      <td>
                        <strong>{i.email}</strong>
                        <span className="correo">
                          invitada por {i.invitadaPor} · {cuandoVence(i.expira)}
                        </span>
                      </td>
                      <td>
                        <span className={`marca rol-${i.rol}`}>{i.rol}</span>
                      </td>
                      <td>
                        <button className="enlace" onClick={() => void revocar(i.id)}>
                          revocar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Tabla>
            </>
          )}
        </div>

        <form className="invitar" onSubmit={(e) => void invitar(e)}>
          <h3>Invitar a alguien</h3>
          <label>
            <span>Correo</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="persona@empresa.cl"
              required
            />
          </label>
          <label>
            <span>Rol</span>
            <select value={rol} onChange={(e) => setRol(e.target.value)}>
              {ROLES.map((r) => (
                <option key={r.valor} value={r.valor}>
                  {r.etiqueta}
                </option>
              ))}
            </select>
          </label>
          <button type="submit">Enviar invitación</button>
          {aviso && <p className={enlaceNuevo && !enlaceNuevo.correoEnviado ? 'error' : 'aviso-ok'}>{aviso}</p>}
          {errorInvitar && <p className="error">{errorInvitar}</p>}
          {enlaceNuevo && (
            <div className="secreto-nuevo">
              <p>Enlace de la invitación — se muestra solo ahora. Vale 7 días y se usa una vez.</p>
              <code>{enlaceNuevo.enlace}</code>
              <button type="button" onClick={() => void navigator.clipboard?.writeText(enlaceNuevo.enlace)}>
                Copiar
              </button>
            </div>
          )}
          <p className="tenue">
            Recibe un enlace válido por 7 días, de un solo uso. Si ya tiene cuenta en la plataforma,
            se le suma este tenant sin tocar su contraseña.
          </p>
        </form>
      </div>
    </section>
  );
}
